import { useCallback, useEffect, useRef, useState } from 'react';
import type { ElicitationAnswer, ElicitationRequest } from '@rally/agent';
import { api } from '../api.ts';
import type { OrganizerView } from '../api.ts';
import { RallyHost } from '../host.ts';
import type { HostEvent } from '../host.ts';
import { McpAppFrame } from '../components/McpAppFrame.tsx';

/**
 * The organizer's private planning link. When the Echo can't tell voices
 * apart, a surprise can't be planned out loud, so Rally texts the organizer
 * this page instead. It is an MCP client like the Echo, and its link acts as
 * the organizer: the same tools, the same card and the same confirmations,
 * on a screen only they hold.
 */

type Line = { role: 'user' | 'alexa' | 'system'; text: string };

const SUGGESTIONS = ["How's it going?", 'Send the invites', 'Find a place', 'Book the first one'];

export function Organizer({ token }: { token: string }) {
  const [view, setView] = useState<OrganizerView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [ui, setUi] = useState<{ uri: string; toolName: string; data?: Record<string, any> } | null>(null);
  const [sheet, setSheet] = useState<{ request: ElicitationRequest; auto: boolean; answer: (a: ElicitationAnswer) => void } | null>(null);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [paying, setPaying] = useState<'idle' | 'sheet' | 'paying'>('idle');
  const hostRef = useRef<RallyHost | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.organizer(token));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [token]);

  const onEvent = useCallback((e: HostEvent) => {
    if (e.type === 'user') setLines((l) => [...l, { role: 'user', text: e.text }]);
    if (e.type === 'alexa') setLines((l) => [...l, { role: 'alexa', text: e.text }]);
    if (e.type === 'system') setLines((l) => [...l, { role: 'system', text: e.text }]);
    if (e.type === 'agent' && e.event.type === 'ui') setUi({ uri: e.event.resourceUri, toolName: e.event.toolName, data: e.event.data });
    if (e.type === 'elicitation') setSheet({ request: e.request, auto: e.auto, answer: e.answer });
    if (e.type === 'elicitation_done') setSheet(null);
    if (e.type === 'busy') setBusy(e.on);
  }, []);

  useEffect(() => {
    void load();
    const host = new RallyHost({ household: '', speaker: '', link: token, era: 'auto', planner: 'offline', onEvent });
    hostRef.current = host;
    // Open on the plan itself, the way the Echo would show it to the organizer.
    void host.say("How's it going?");
    return () => {
      void host.close();
      if (hostRef.current === host) hostRef.current = null;
    };
  }, [token, load, onEvent]);

  const send = useCallback(
    async (text: string) => {
      const host = hostRef.current;
      if (!host || !text.trim()) return;
      if (/^pay for this order$/i.test(text.trim())) {
        await load();
        return setPaying('sheet');
      }
      await host.say(text.trim());
      void load();
    },
    [load],
  );

  async function pay() {
    const deposit = view?.hostDeposit;
    if (!deposit || !view) return;
    setPaying('paying');
    const r = await api.payDeposit(deposit.itemId, view.organizer, token);
    setPaying('idle');
    setLines((l) => [...l, { role: 'system', text: r.status === 'completed' ? 'Deposit paid to the venue.' : r.error ?? 'Payment failed' }]);
    await load();
    await hostRef.current?.say("How's it going?");
  }

  if (error) return <div className="phone-page"><div className="phone-card"><h1>Hmm.</h1><p>{error}</p></div></div>;
  if (!view) return <div className="phone-page"><div className="phone-card">Loading your plan…</div></div>;

  return (
    <div className="phone-page">
      <div className="phone-card organizer">
        <div className="phone-brand">Rally</div>
        <p className="phone-kicker">Only you can see this, {view.organizer}</p>
        <h1>{view.title}</h1>
        {view.surpriseFor.length > 0 && <div className="shh">🤫 Nothing about it plays on the Echo, and nothing goes to {view.surpriseFor.join(' or ')}.</div>}

        <div className="org-card">
          {ui && hostRef.current ? (
            <McpAppFrame
              key={ui.uri}
              callTool={(name, args) => hostRef.current!.viewCallTool(name, args)}
              readResource={(uri) => hostRef.current!.readResource(uri)}
              resourceUri={ui.uri}
              toolName={ui.toolName}
              data={ui.data}
              size="show8"
              theme="dark"
              onMessage={(text) => void send(text)}
            />
          ) : (
            <div className="muted">Loading the plan…</div>
          )}
        </div>

        <div className="org-chat" aria-live="polite">
          {lines.slice(-6).map((l, i) => (
            <div key={i} className={`line ${l.role}`}>
              {l.role === 'alexa' ? <b>Rally </b> : l.role === 'user' ? <b>You </b> : null}
              {l.text}
            </div>
          ))}
          {busy && <div className="line system">…</div>}
        </div>

        {sheet && (
          <div className="org-confirm" role="dialog" aria-label="Confirm">
            <p>{sheet.request.message}</p>
            {sheet.auto ? (
              <div className="ok">✓ Confirmed by what you asked</div>
            ) : (
              <div className="row">
                <button onClick={() => sheet.answer({ action: 'accept', content: { confirm: true } })}>Confirm</button>
                <button className="ghost" onClick={() => sheet.answer({ action: 'decline' })}>
                  Not now
                </button>
              </div>
            )}
          </div>
        )}

        {paying !== 'idle' && view.hostDeposit?.status === 'due' && (
          <div className="paysheet">
            <div>
              <b>Amazon Pay</b> (simulated) · Card •••• 4242
            </div>
            <button disabled={paying === 'paying'} onClick={() => void pay()}>
              {paying === 'paying' ? 'Paying…' : `Pay $${(view.hostDeposit.amountCents / 100).toFixed(2)} to the venue`}
            </button>
            <button className="ghost" onClick={() => setPaying('idle')}>
              Cancel
            </button>
          </div>
        )}

        <div className="chips">
          {SUGGESTIONS.map((s) => (
            <button key={s} className="chip" disabled={busy} onClick={() => void send(s)}>
              {s}
            </button>
          ))}
        </div>
        <form
          className="say"
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
            setInput('');
          }}
        >
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Tell Rally, e.g. tell everyone to arrive by 6:45" />
          <button type="submit" disabled={busy}>
            Send
          </button>
        </form>
        <p className="fine">This link acts as you. Don’t forward it. The Echo in the kitchen only ever says it’s sworn to secrecy.</p>
      </div>
    </div>
  );
}
